'use client';

import React from 'react';
import toast from 'react-hot-toast';

import { Tooltip } from '@benzinga/core-ui';
import { AuthenticationManager } from '@benzinga/session';
import { SessionContext } from '@benzinga/session-context';
import { NewsAlertsManager } from '@benzinga/news-alerts-manager';
import { FollowAuthor } from '@benzinga/icons';

interface Props {
  authorName: string;
  authorUid: number;
  variant?: 'icon' | 'text' | 'large' | 'transparent';
  className?: string;
}

export const FollowAuthorButton: React.FC<Props> = ({ authorName, authorUid, className, variant = 'icon' }: Props) => {
  const session = React.useContext(SessionContext);
  const newsAlertsManager = session.getManager(NewsAlertsManager);
  const isLoggedIn = session.getManager(AuthenticationManager).isLoggedIn();

  const handleFollow = async () => {
    if (!isLoggedIn) {
      toast.error(`Please log in to subscribe to ${authorName}.`);
      return;
    }
    const res = await newsAlertsManager.createAuthorSubscription(authorUid, authorName);
    if (res.ok) {
      toast.success(`You are now subscribed to new articles from ${authorName}.`, {
        duration: 3000,
      });
    } else {
      toast.error('Failed to subscribe to the author. Please try again later.');
    }
  };

  const getContainerClasses = () => {
    const classes = ['cursor-pointer'];
    if (variant === 'large') {
      classes.push('bg-black w-[30px] h-[30px] rounded flex justify-center items-center hover:-translate-y-1');
    }
    if (variant === 'transparent') {
      classes.push('border border-[#373f49] rounded');
    }
    if (className) {
      classes.push(className);
    }
    return classes.join(' ');
  };

  const getIconClasses = () => {
    if (variant === 'transparent') {
      return '[&_rect:first-child]:fill-transparent';
    }
    return '[&_rect:first-child]:fill-black';
  };

  return (
    <div className={getContainerClasses()} onClick={handleFollow}>
      {variant === 'text' ? (
        <span className="font-semibold">Follow</span>
      ) : (
        <Tooltip content="Follow Author">
          <FollowAuthor className={getIconClasses()} />
        </Tooltip>
      )}
    </div>
  );
};
